import React from 'react';

export type UpgradeRequestItem = {
  id: string;
  uid: string;
  email?: string | null;
  name?: string | null;
  currentPlan?: string;
  requestedPlan: 'basic' | 'premium' | string;
  message?: string;
  status: 'new' | 'approved' | 'rejected' | string;
  createdAtMs?: number;
};

export type ContactMessageItem = {
  id: string;
  uid?: string | null;
  email?: string | null;
  replyToEmail?: string | null;
  name?: string | null;
  formName?: string | null;
  type?: string;
  message: string;
  status: 'new' | 'read' | 'closed' | string;
  createdAtMs?: number;
};

type Props = {
  requests: UpgradeRequestItem[];
  contactMessages: ContactMessageItem[];
  loading: boolean;
  contactMessagesLoading: boolean;
  actionLoadingId: string | null;
  onRefresh: () => void;
  onApprove: (request: UpgradeRequestItem) => void;
  onReject: (request: UpgradeRequestItem) => void;
  onUpdatePlan: (uid: string, plan: 'free' | 'basic' | 'premium') => Promise<void>;
  onAddCoins: (uid: string, feature: string, amount: number) => Promise<void>;
};

const planOptions = ['free', 'basic', 'premium'] as const;
const coinFeatures = [
  { value: 'searchVolume', label: '検索ボリューム' },
  { value: 'relatedKeywords', label: '関連キーワード' },
  { value: 'aiInsights', label: 'AIインサイト' },
  { value: 'aiPersonas', label: 'ペルソナ生成' },
  { value: 'personaImages', label: 'ペルソナ画像' },
];

const contactTypeLabels: Record<string, string> = {
  bug: '不具合',
  'paid-plan': '有料プラン相談',
  'feature-request': '機能要望',
  other: 'その他',
};

const formatDate = (value?: number) => {
  if (!value) return '-';

  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const AdminPage: React.FC<Props> = ({
  requests,
  contactMessages,
  loading,
  contactMessagesLoading,
  actionLoadingId,
  onRefresh,
  onApprove,
  onReject,
  onUpdatePlan,
  onAddCoins,
}) => {
  const [uid, setUid] = React.useState('');
  const [plan, setPlan] = React.useState<'free' | 'basic' | 'premium'>('basic');
  const [coinUid, setCoinUid] = React.useState('');
  const [coinFeature, setCoinFeature] = React.useState('searchVolume');
  const [coinAmount, setCoinAmount] = React.useState(1);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [manualLoading, setManualLoading] = React.useState(false);

  const handlePlanSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const targetUid = uid.trim();
    if (!targetUid) return;

    setManualLoading(true);
    setNotice(null);
    try {
      await onUpdatePlan(targetUid, plan);
      setNotice(`UID ${targetUid} を ${plan} に変更しました。`);
      setUid('');
    } finally {
      setManualLoading(false);
    }
  };

  const handleCoinsSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const targetUid = coinUid.trim();
    if (!targetUid || coinAmount <= 0) return;

    setManualLoading(true);
    setNotice(null);
    try {
      await onAddCoins(targetUid, coinFeature, coinAmount);
      setNotice(`UID ${targetUid} に ${coinFeature} コインを ${coinAmount} 回分付与しました。`);
      setCoinUid('');
      setCoinAmount(1);
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-6">
      <div className="mb-6 flex flex-col gap-3 border-b border-gray-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm font-semibold text-blue-600">Admin</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">管理画面</h1>
          <p className="mt-2 text-sm text-gray-500">
            先行利用申請、お問い合わせ、手動プラン変更、追加コイン付与を管理します。
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="w-fit rounded border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          再読み込み
        </button>
      </div>

      {notice && (
        <div className="mb-6 rounded border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
          {notice}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">手動プラン変更</h2>
          <form onSubmit={handlePlanSubmit} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px_auto]">
            <input
              value={uid}
              onChange={(event) => setUid(event.target.value)}
              placeholder="ユーザーUID"
              className="rounded border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            />
            <select
              value={plan}
              onChange={(event) => setPlan(event.target.value as typeof plan)}
              className="rounded border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-300"
            >
              {planOptions.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={manualLoading}
              className="rounded bg-blue-500 px-4 py-2 text-sm font-bold text-white hover:bg-blue-600 disabled:opacity-60"
            >
              変更
            </button>
          </form>
        </section>

        <section className="rounded border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">追加コイン付与</h2>
          <form onSubmit={handleCoinsSubmit} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_150px_90px_auto]">
            <input
              value={coinUid}
              onChange={(event) => setCoinUid(event.target.value)}
              placeholder="ユーザーUID"
              className="rounded border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            />
            <select
              value={coinFeature}
              onChange={(event) => setCoinFeature(event.target.value)}
              className="rounded border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-300"
            >
              {coinFeatures.map((feature) => (
                <option key={feature.value} value={feature.value}>{feature.label}</option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              value={coinAmount}
              onChange={(event) => setCoinAmount(Number(event.target.value))}
              className="rounded border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-300"
            />
            <button
              type="submit"
              disabled={manualLoading}
              className="rounded bg-blue-500 px-4 py-2 text-sm font-bold text-white hover:bg-blue-600 disabled:opacity-60"
            >
              付与
            </button>
          </form>
        </section>
      </div>

      <section className="rounded border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">先行利用申請</h2>
            <p className="mt-1 text-sm text-gray-500">
              承認すると、申請者のプランが希望プランに変更されます。
            </p>
          </div>
          {loading && <span className="text-xs font-semibold text-gray-400">読み込み中...</span>}
        </div>

        <div className="mt-4 space-y-3">
          {!loading && requests.length === 0 && (
            <div className="rounded border border-dashed border-gray-200 px-3 py-8 text-center text-sm text-gray-500">
              申請はありません
            </div>
          )}
          {requests.map((request) => (
            <div key={request.id} className="rounded border border-gray-200 bg-white p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold text-gray-900">{request.email || request.uid}</p>
                    <span className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                      {request.status}
                    </span>
                    <span className="rounded bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                      {request.currentPlan || 'free'} → {request.requestedPlan}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">UID: {request.uid}</p>
                  <p className="mt-1 text-xs text-gray-500">申請日時: {formatDate(request.createdAtMs)}</p>
                  {request.message && (
                    <p className="mt-3 rounded bg-slate-50 px-3 py-2 text-sm leading-6 text-gray-700">
                      {request.message}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    disabled={actionLoadingId === request.id || request.status === 'approved'}
                    onClick={() => onApprove(request)}
                    className="rounded bg-blue-500 px-4 py-2 text-sm font-bold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    承認
                  </button>
                  <button
                    type="button"
                    disabled={actionLoadingId === request.id || request.status === 'rejected'}
                    onClick={() => onReject(request)}
                    className="rounded border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    却下
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-6 rounded border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">お問い合わせ</h2>
            <p className="mt-1 text-sm text-gray-500">
              フォームから送信された問い合わせを確認します。
            </p>
          </div>
          {contactMessagesLoading && <span className="text-xs font-semibold text-gray-400">読み込み中...</span>}
        </div>

        <div className="mt-4 space-y-3">
          {!contactMessagesLoading && contactMessages.length === 0 && (
            <div className="rounded border border-dashed border-gray-200 px-3 py-8 text-center text-sm text-gray-500">
              お問い合わせはありません
            </div>
          )}
          {contactMessages.map((item) => (
            <div key={item.id} className="rounded border border-gray-200 bg-white p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold text-gray-900">
                      {item.name || item.formName || item.email || item.replyToEmail || '名前なし'}
                    </p>
                    <span className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                      {item.status || 'new'}
                    </span>
                    <span className="rounded bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                      {contactTypeLabels[item.type || 'other'] || item.type || 'その他'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    返信先: {item.replyToEmail || item.email || '-'}
                  </p>
                  {item.uid && <p className="mt-1 text-xs text-gray-500">UID: {item.uid}</p>}
                  <p className="mt-1 text-xs text-gray-500">送信日時: {formatDate(item.createdAtMs)}</p>
                  <p className="mt-3 whitespace-pre-wrap rounded bg-slate-50 px-3 py-2 text-sm leading-6 text-gray-700">
                    {item.message}
                  </p>
                </div>
                {(item.replyToEmail || item.email) && (
                  <a
                    href={`mailto:${item.replyToEmail || item.email}`}
                    className="shrink-0 rounded border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
                  >
                    返信
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default AdminPage;
